import isNil from 'lodash-es/isNil';
import { gql } from '@yi/core';
import { PLAN_SKUS, PlanSku } from '@yi/core/src/gaia';

import { TPlan, ZERO_PRICE } from '~/src/common/brooklyn/plans';
import { MONTHS_IN_YEAR } from '~/src/common/constants';

import { Price } from '../../price/Price';

export type PlanPeriod = '3-month' | 'month' | 'year';

export class Plan {
  planDetails: TPlan;

  constructor(planDetails: TPlan) {
    this.planDetails = planDetails;
  }
  static isValidPlanDetails(planDetails: undefined | TPlan | null): planDetails is TPlan {
    return !isNil(planDetails);
  }

  interval() {
    return this.planDetails.interval;
  }
  displayPrice() {
    return this.planDetails.displayPrice;
  }

  monthlyDisplayPrice(): string {
    if (this.interval() === gql.typesClient.MembershipPlanInterval.MONTH) {
      return this.displayPrice();
    }

    const price = Number(this.price());
    const monthlyPrice = (price / MONTHS_IN_YEAR).toFixed(2);
    return `${
      this.isCurrencyISORequired() ? `${this.currencyIso()} ` : ''
    }${this.currencySymbol()}${monthlyPrice}`;
  }

  currencyIso() {
    return this.planDetails.currencyIso;
  }
  currencySymbol() {
    return this.planDetails.currencySymbol;
  }

  annualPrice() {
    const price = this.price();
    if (isNil(price)) {
      return null;
    }
    if (this.interval() === gql.typesClient.MembershipPlanInterval.MONTH) {
      const calculatedAnnualPrice = price * MONTHS_IN_YEAR;
      return calculatedAnnualPrice;
    }

    return price;
  }

  monthlyPrice(numberOfMonth = 1) {
    // NOTE: this works only for regular plans, not discounted
    const price = this.price();
    if (isNil(price)) {
      return null;
    }
    if (this.interval() === gql.typesClient.MembershipPlanInterval.YEAR) {
      const calculatedMonthlyPrice = price / MONTHS_IN_YEAR;
      return calculatedMonthlyPrice * numberOfMonth;
    }

    return price * numberOfMonth;
  }

  sku() {
    return this.planDetails.sku;
  }
  country() {
    return this.planDetails.country;
  }
  name() {
    return this.planDetails.name;
  }

  hasFreeTrial() {
    return this.planDetails.costs[0] === ZERO_PRICE;
  }

  tier() {
    return this.planDetails.tier;
  }

  private extractPrice(): string | null | undefined {
    // plan with free trial has two values [trialPayment, price], where 'trialPayment' is "0.00"
    const price = this.planDetails.costs.find(c => Number(c) !== 0);
    return price;
  }

  price(): number | null {
    const costStr = this.extractPrice();
    if (isNil(costStr)) return null;
    return parseFloat(costStr);
  }

  priceStr(): string {
    const costStr = this.extractPrice();

    if (isNil(costStr)) return '';
    return costStr;
  }

  renewPrice(): number | null {
    // plan with renew price has two values [price, renewPrice]
    const renewPriceString = this.planDetails.costs?.[1] ?? null;
    if (renewPriceString === null) {
      return null;
    }
    const renewPriceNumber = parseFloat(renewPriceString);

    if (isNaN(renewPriceNumber)) {
      return null;
    }

    if (this.hasFreeTrial()) {
      return null;
    }

    return renewPriceNumber;
  }

  static toDetailedPrice(price: number): {
    dollars: string;
    cents: string;
  } {
    const dollars = Math.floor(price);
    const cents = Math.round((price - dollars) * 100);
    return {
      dollars: String(dollars),
      cents: Plan.getDispayCents(cents),
    };
  }

  detailedPrice(): {
    dollars: string;
    cents: string;
  } | null {
    const price = this.price();
    if (price === null) {
      return null;
    }
    return Plan.toDetailedPrice(price);
  }

  detailedAnnualPrice(): {
    dollars: string;
    cents: string;
  } | null {
    const price = this.annualPrice();
    if (price === null) {
      return null;
    }

    return Plan.toDetailedPrice(price);
  }

  detailedRenewPrice(): {
    dollars: string;
    cents: string;
  } | null {
    const price = this.renewPrice();
    if (price === null) {
      return null;
    }

    const dollars = Math.floor(price);
    const cents = Math.round((price - dollars) * 100);
    return {
      dollars: String(dollars),
      cents: Plan.getDispayCents(cents),
    };
  }

  isCurrencyISORequired(): boolean {
    return Price.isCurrencyISORequired({
      currencySymbol: this.currencySymbol(),
      currencyIso: this.currencyIso(),
    });
  }

  private static getDispayCents(cents: number): string {
    if (cents < 10) {
      return `0${cents}`;
    }
    return String(cents);
  }

  get() {
    return this.planDetails;
  }

  isSkuEqualTo(sku: PlanSku): boolean {
    return this.sku() === sku;
  }

  planPeriod(): PlanPeriod | null {
    if (
      this.sku() === PLAN_SKUS.BASIC_3_MONTH_PROMO ||
      this.sku() === PLAN_SKUS.BASIC_3_MONTH_CANCEL_DEFLECT
    ) {
      return '3-month';
    } else if (this.interval() === gql.typesClient.MembershipPlanInterval.MONTH) {
      return 'month';
    } else if (this.interval() === gql.typesClient.MembershipPlanInterval.YEAR) {
      return 'year';
    }
    return null;
  }

  static getSavingsPercentage({
    basePlanDetails,
    discountPlanDetails,
  }: {
    basePlanDetails: TPlan | null;
    discountPlanDetails: TPlan | null;
  }) {
    if (isNil(basePlanDetails) || isNil(discountPlanDetails)) {
      return 0;
    }
    const regularPlan = new Plan(basePlanDetails);
    const discountPlan = new Plan(discountPlanDetails);

    const discountPlanPrice = discountPlan.price();

    if (isNil(discountPlanPrice)) {
      return 0;
    }

    if (
      discountPlan.sku() === PLAN_SKUS.BASIC_3_MONTH_PROMO ||
      discountPlan.sku() === PLAN_SKUS.BASIC_3_MONTH_CANCEL_DEFLECT
    ) {
      const regularMonthlyPrice = regularPlan.monthlyPrice();

      if (isNil(regularMonthlyPrice)) {
        return 0;
      }

      const regular3MonthlyPrice = regularMonthlyPrice * 3;
      const savingsPercentage = Price.getSavingsPercentage(regular3MonthlyPrice, discountPlanPrice);
      return savingsPercentage;
    }

    // TODO add logic here
    return 0;
  }
}
